import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import apiService from '../services/apiService'
import { BookOpen, Check } from 'lucide-react'

const StatusBadge = ({ status }) => {
  if (status === 'None' || !status) {
    return (
      <span className="inline-flex items-center justify-center px-3 py-1.5 rounded-full text-sm font-medium bg-[rgba(233,0,4,0.10)] text-[#E90004] whitespace-nowrap">
        Not Assigned
      </span>
    )
  }
  return (
    <span className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-[rgba(4,146,185,0.1)] text-[rgba(4,146,185,1)] whitespace-nowrap">
      <Check className="w-4 h-4" />
      Assigned
    </span>
  )
}

function AssignCourseBatchList() {
  const [data, setData] = useState([])
  const [courses, setCourses] = useState([])
  const [loading, setLoading] = useState(true)
  const { id } = useParams()
  const navigate = useNavigate()
  const api = apiService()

  useEffect(() => {
    if (id) {
      getCourses()
    }
  }, [id])

  const getCourses = async () => {
    try {
      setLoading(true)
      const response = await api.get(`batches/${id}/courses`)
      if (response.status === 200) {
        setData(response.data.courses || [])
      }
    } catch (error) {
      console.error('Error fetching batch courses:', error)
      toast.error('Failed to fetch courses')
    } finally {
      setLoading(false)
    }
  }

  const checkCourse = (courseId) => {
    const index = courses.indexOf(courseId)
    const coursesTemp = courses.slice()
    if (index > -1) {
      coursesTemp.splice(index, 1)
    } else {
      coursesTemp.push(courseId)
    }
    setCourses(coursesTemp)
  }

  const handleAssign = async (e) => {
    e.preventDefault()
    const toAssign = courses.filter((cid) => {
      const item = data.find((c) => c._id === cid)
      return item && !item.assigned
    })
    if (toAssign.length === 0) {
      toast.error('Please select at least one course to assign')
      return
    }
    try {
      const response = await api.post('assigncoursesbatch', {
        batch_id: id,
        courses: toAssign,
      })
      if (response.status === 201) {
        toast.success('Courses assigned to batch successfully')
        setCourses([])
        await getCourses()
      }
    } catch (error) {
      console.error('Error assigning courses:', error)
      toast.error(error.response?.data?.message || 'Failed to assign courses')
    }
  }

  const handleUnassign = async (e) => {
    e.preventDefault()
    const toUnassign = courses.filter((cid) => {
      const item = data.find((c) => c._id === cid)
      return item && item.assigned
    })
    if (toUnassign.length === 0) {
      toast.error('Please select at least one assigned course to unassign')
      return
    }
    try {
      const response = await api.post('delete_batch_course', {
        batch_id: id,
        courses: toUnassign,
      })
      if (response.status === 200) {
        toast.success('Courses unassigned from batch successfully')
        setCourses([])
        await getCourses()
      }
    } catch (error) {
      console.error('Error unassigning courses:', error)
      toast.error(error.response?.data?.message || 'Failed to unassign courses')
    }
  }

  const mappedData = data.map((item, index) => ({
    id: item._id,
    sNo: String(index + 1).padStart(2, '0'),
    courseName: item.title,
    description: item.description || 'No description',
    status: item.assigned ? 'Assigned' : 'None',
    isActive: item.assigned,
    isSelected: courses.includes(item._id),
    raw: item,
  }))

  const hasAssignedSelected = courses.some((cid) => {
    const item = data.find((c) => c._id === cid)
    return item && item.assigned
  })

  if (loading) {
    return (
      <div className="w-full mt-2.5 max-md:max-w-full">
        <div className="bg-white border border-[#F0F0F0] rounded-[10px] p-8">
          <div className="text-center">Loading courses...</div>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full mt-2.5 rounded-[10px] border border-[#F0F0F0] bg-white overflow-hidden shadow-sm">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 px-5 py-4 border-b border-[#F0F0F0] bg-white">
        <h2 className="text-[#030303] text-lg font-bold m-0">
          Assign Courses to Batch
        </h2>
        <div className="flex gap-2">
          <button
            className="px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-[#DE3C7D] hover:bg-[#c72c6a] transition-colors"
            type="button"
            onClick={() => navigate('/batches')}
          >
            Back to Batches
          </button>
          <button
            className="px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-[#DE3C7D] hover:bg-[#c72c6a] transition-colors"
            type="button"
            onClick={handleAssign}
          >
            Assign Courses
          </button>
          <button
            className={
              hasAssignedSelected
                ? 'px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-[#DE3C7D] hover:bg-[#c72c6a] transition-colors'
                : 'px-4 py-2.5 rounded-lg text-sm font-medium text-[#535862] bg-neutral-200 cursor-not-allowed transition-colors'
            }
            type="button"
            disabled={!hasAssignedSelected}
            onClick={handleUnassign}
          >
            Unassign Selected
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <colgroup>
            <col style={{ width: '56px' }} />
            <col style={{ width: '64px' }} />
            <col style={{ width: '38%' }} />
            <col style={{ width: '220px' }} />
          </colgroup>
          <thead>
            <tr className="bg-neutral-50">
              <th className="text-left text-xs font-medium text-[#535862] px-4 py-3.5 border-b border-r border-[#F0F0F0]">
                <span className="sr-only">Select</span>
              </th>
              <th className="text-center text-xs font-medium text-[#535862] px-4 py-3.5 border-b border-r border-[#F0F0F0]">
                S.No
              </th>
              <th className="text-left text-xs font-medium text-[#535862] px-4 py-3.5 border-b border-r border-[#F0F0F0]">
                Course Name
              </th>
              <th className="text-center text-xs font-medium text-[#535862] px-4 py-3.5 border-b border-[#F0F0F0]">
                Status
              </th>
            </tr>
          </thead>
          <tbody>
            {mappedData.length === 0 ? (
              <tr>
                <td colSpan={4} className="p-8 text-center border-b border-[#F0F0F0]">
                  <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-[#535862] m-0">No courses available</p>
                </td>
              </tr>
            ) : (
              mappedData.map((item, index) => (
                <tr
                  key={item.id}
                  className={index % 2 === 0 ? 'bg-white' : 'bg-neutral-50/50'}
                >
                  <td className="px-4 py-3 border-b border-r border-[#F0F0F0] align-middle">
                    <div className="flex items-center justify-center min-h-[1.5rem]">
                      <input
                        type="checkbox"
                        checked={item.isSelected}
                        onChange={() => checkCourse(item.id)}
                        className="w-4 h-4 m-0 shrink-0 rounded border-gray-300 cursor-pointer align-middle accent-[#DE3C7D] focus:ring-[#DE3C7D] focus:ring-offset-0"
                      />
                    </div>
                  </td>
                  <td className="px-4 py-3 border-b border-r border-[#F0F0F0] text-center text-sm text-[#030303] align-middle">
                    {item.sNo}
                  </td>
                  <td className="px-4 py-3 border-b border-r border-[#F0F0F0] text-sm text-[#030303] font-medium align-middle max-w-0">
                    <span className="block truncate" title={item.courseName}>{item.courseName}</span>
                  </td>
                  <td className="px-4 py-3 border-b border-[#F0F0F0] text-center align-middle">
                    <div className="flex justify-center">
                      <StatusBadge status={item.status} />
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default AssignCourseBatchList
